import { PaymentIntent } from "@stripe/stripe-js";
import React, { createContext, useContext, useState } from "react";
import { Task } from "src/types/tasks";

interface TaskScheduleInfo {
  date: string;
  time: string;
  instructions: string;
}

interface TaskContactInfo {
  fullname: string;
  email: string;
  phone: string;
  contactType: string;
  address: string;
  apartmentNo: string;
}

interface TasksContextType {
  selectedTasks: Task[];
  setSelectedTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  scheduleInfo: TaskScheduleInfo;
  setScheduleInfo: React.Dispatch<React.SetStateAction<TaskScheduleInfo>>;
  contactInfo: TaskContactInfo;
  setContactInfo: React.Dispatch<React.SetStateAction<TaskContactInfo>>;
  taskPage: number;
  setTaskPage: React.Dispatch<React.SetStateAction<number>>;
  paymentIntent: PaymentIntent | null;
  setPaymentIntent: React.Dispatch<React.SetStateAction<PaymentIntent | null>>;
  tipAmount: number;
  setTipAmount: React.Dispatch<React.SetStateAction<number>>;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

const initialScheduleInfo: TaskScheduleInfo = {
  date: "",
  time: "",
  instructions: "",
};

const initialContactInfo: TaskContactInfo = {
  fullname: "",
  email: "",
  phone: "",
  contactType: "",
  address: "",
  apartmentNo: "",
};

export const TasksProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [scheduleInfo, setScheduleInfo] =
    useState<TaskScheduleInfo>(initialScheduleInfo);
  const [contactInfo, setContactInfo] =
    useState<TaskContactInfo>(initialContactInfo);
  const [taskPage, setTaskPage] = useState<number>(1);
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(
    null
  );
  const [tipAmount, setTipAmount] = useState<number>(0);

  return (
    <TasksContext.Provider
      value={{
        selectedTasks,
        setSelectedTasks,
        scheduleInfo,
        setScheduleInfo,
        contactInfo,
        setContactInfo,
        taskPage,
        setTaskPage,
        paymentIntent,
        setPaymentIntent,
        tipAmount,
        setTipAmount,
      }}
    >
      {children}
    </TasksContext.Provider>
  );
};

export const useTasks = () => {
  const context = useContext(TasksContext);
  if (context === undefined) {
    throw new Error("useTasks must be used within a TasksProvider");
  }
  return context;
};
